/**
 * A tool's identity on its directory card: brand mark, then wordmark.
 *
 * The wordmark alone was the whole heading, which meant the card had no glyph
 * at all — a grid of twelve cards distinguished only by a word set in the same
 * typeface, scanned as twelve identical cards. The mark is what makes one
 * findable at a glance, and it is the same mark the storefront and the
 * companion-app step use, so a tool is one object across the three.
 *
 * Five of the twelve tools have a mark. The rest fall back to the emoji they
 * showed before wordmarks existed, so every card carries something in that
 * slot and none of them carries a placeholder box.
 *
 * Every entry ships a wordmark at /assets/logos/<id>.svg, so swapping a
 * placeholder for a company's real brand asset is a file drop with no code
 * change. The files in the repo today are plain generated wordmarks (correct
 * name, correct dimensions, no brand identity) and are meant to be replaced.
 *
 * Four rules for whatever replaces them:
 *
 *   - The logo must contain the product name. It is the card's heading, and a
 *     bare glyph leaves the card unreadable to anyone who does not already
 *     recognise it. That holds even now the mark sits beside it: the mark is
 *     recognition, the wordmark is the name.
 *   - Self-host it. Nothing here hotlinks a logo from the vendor's site; the
 *     two assets this site lost to the Manus CDN shutdown are the reason.
 *   - SVG, transparent background, sized for a 40px box, and no wider than
 *     235 units. The mark now shares the header with it, so max-w-[165px]
 *     is what is left: at h-7 that caps the intrinsic width at 236. Gemini
 *     Notebook is 233 and is the reason the number is not lower. Go past it
 *     and the wordmark silently renders smaller than every other card's.
 *   - The wordmark must actually FIT its own viewBox. Anything past the
 *     viewBox is clipped silently — no console warning, no layout shift, and
 *     the card still looks deliberate. Gamma read "Gamm" on every card for as
 *     long as the file existed, because the generated width was a
 *     characters-times-average estimate and "Gamma" carries two m's. ChatGPT
 *     was losing 0.9px off its final T the same way. Measure the rendered text
 *     rather than trusting the number in the file.
 *
 * If the wordmark file is missing or fails to load, the card falls back to the
 * name in text beside the same mark. The name is in the accessible name either
 * way.
 */

import { useState } from "react";
import BrandMark, { hasBrandMark } from "@/components/BrandMark";
import type { Tool } from "@/lib/toolsData";

export default function ToolLogo({ tool }: { tool: Tool }) {
  const [failed, setFailed] = useState(false);
  const marked = hasBrandMark(tool.id);

  return (
    /* items-start so the mark hangs from the same line as the name, matching
       the Resource Center cards. The wordmark is a fixed-height image so this
       looks identical to centring today — it stops differing the moment a
       fallback name wraps to two lines. */
    <div className="flex items-start gap-2.5 min-w-0">
      {marked ? (
        <BrandMark brandId={tool.id} size={28} />
      ) : (
        <span className="text-2xl leading-none" aria-hidden="true">
          {tool.emoji}
        </span>
      )}

      {failed || !tool.logo ? (
        <h3 className="font-['Fraunces'] text-base font-semibold text-foreground leading-tight">
          {tool.name}
        </h3>
      ) : (
        <h3 className="leading-none min-w-0">
          <img
            src={tool.logo}
            alt={tool.name}
            loading="lazy"
            onError={() => setFailed(true)}
            className="h-7 w-auto max-w-[165px] object-contain object-left"
          />
        </h3>
      )}
    </div>
  );
}
